import { baseApi } from './baseApi'

import {
  AssignBillingDenialsDto,
  BillingDenialBulkResult,
  BillingDenialDto,
  BillingDenialReasonSummary,
  BillingDenialWorklist,
  BillingDenialWorklistFilters,
  BillingDenialWorklistQuery,
  MoveBillingDenialsDto,
  RecordEraDenialDto,
  RecordManualDenialDto,
  ResolveBillingDenialDto,
  ResolveBillingDenialsDto,
  ResubmitBillingDenialsDto,
} from 'store/types'

type DenialAddress = { patientId: number; denialId: string }

// Recording or resolving a denial moves a claim off the ERA lists, money
// onto the Payments tab, and the Denials menu count.
const DENIAL_CHANGE_TAGS = [
  'BillingDenials',
  'OrderCounts',
  'BillingTransactionClaims',
  'BillingEraTransactions',
  'BillingEra',
  'EraExceptionQueue',
  'BillingPatientPayments',
  'BillingCharges',
] as const

export const billingDenialsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getChargeDenials: builder.query<BillingDenialDto[], { patientId: number; chargeId: string }>({
      query: ({ patientId, chargeId }) => ({
        url: `/lab/patients/${patientId}/account/charges/${chargeId}/denials`,
      }),
      providesTags: ['BillingDenials'],
    }),
    recordEraDenial: builder.mutation<BillingDenialDto, RecordEraDenialDto>({
      query: ({ eraId, claimId, ...body }) => ({
        url: `/lab/billing/era-reports/${eraId}/claims/${claimId}/record-denial`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { claimId }) => [
        ...DENIAL_CHANGE_TAGS,
        { type: 'EraPostingPlan' as const, id: claimId },
      ],
    }),
    recordManualDenial: builder.mutation<BillingDenialDto, RecordManualDenialDto>({
      query: (body) => ({
        url: `/lab/patients/${body.patientId}/account/denials`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [...DENIAL_CHANGE_TAGS],
    }),
    resolveDenial: builder.mutation<BillingDenialDto, DenialAddress & ResolveBillingDenialDto>({
      query: ({ patientId, denialId, ...body }) => ({
        url: `/lab/patients/${patientId}/account/denials/${denialId}/resolve`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { patientId }) => [
        ...DENIAL_CHANGE_TAGS,
        { type: 'Patient', id: Number(patientId) },
      ],
    }),
    getDenialReasonSummary: builder.query<BillingDenialReasonSummary[], BillingDenialWorklistFilters>({
      query: (params) => ({ url: '/lab/billing/denials/reasons', params }),
      providesTags: ['BillingDenials'],
    }),
    getDenialWorklist: builder.query<BillingDenialWorklist, BillingDenialWorklistQuery>({
      query: (params) => ({ url: '/lab/billing/denials', params }),
      providesTags: ['BillingDenials'],
    }),
    // Status and owner changes post nothing.
    assignDenials: builder.mutation<BillingDenialBulkResult, AssignBillingDenialsDto>({
      query: (body) => ({ url: '/lab/billing/denials/assign', method: 'POST', body }),
      invalidatesTags: ['BillingDenials'],
    }),
    appealDenials: builder.mutation<BillingDenialBulkResult, MoveBillingDenialsDto>({
      query: (body) => ({ url: '/lab/billing/denials/appeal', method: 'POST', body }),
      invalidatesTags: ['BillingDenials', 'OrderCounts'],
    }),
    moveDenialsToInProcess: builder.mutation<BillingDenialBulkResult, MoveBillingDenialsDto>({
      query: (body) => ({ url: '/lab/billing/denials/move-to-in-process', method: 'POST', body }),
      invalidatesTags: ['BillingDenials', 'OrderCounts'],
    }),
    resubmitDenials: builder.mutation<BillingDenialBulkResult, ResubmitBillingDenialsDto>({
      query: (body) => ({ url: '/lab/billing/denials/resubmit', method: 'POST', body }),
      invalidatesTags: [...DENIAL_CHANGE_TAGS],
    }),
    // Back to Pending; only the badge count moves.
    reopenDenials: builder.mutation<BillingDenialBulkResult, MoveBillingDenialsDto>({
      query: (body) => ({ url: '/lab/billing/denials/reopen', method: 'POST', body }),
      invalidatesTags: ['BillingDenials', 'OrderCounts'],
    }),
    resolveDenials: builder.mutation<BillingDenialBulkResult, ResolveBillingDenialsDto>({
      query: (body) => ({ url: '/lab/billing/denials/resolve', method: 'POST', body }),
      invalidatesTags: [...DENIAL_CHANGE_TAGS, 'Patient'],
    }),
  }),
})

export const {
  useGetChargeDenialsQuery,
  useRecordEraDenialMutation,
  useRecordManualDenialMutation,
  useResolveDenialMutation,
  useGetDenialReasonSummaryQuery,
  useGetDenialWorklistQuery,
  useAssignDenialsMutation,
  useAppealDenialsMutation,
  useMoveDenialsToInProcessMutation,
  useResubmitDenialsMutation,
  useResolveDenialsMutation,
  useReopenDenialsMutation,
} = billingDenialsApi
